/**
 * Mark's invite — one home for sending it and for reading whether it went
 * (signup-invite-simple). The invite is the isInviteCta chat message in the
 * connection's room; the server refuses it from anyone but an admin account
 * (migration 041) and converts only invited people.
 *
 * Callers log `logFunnelInviteSent` themselves (analytics stay at call sites).
 * The receiving side (a Simple Profile's People page) asks the server for its
 * invite, and asks again when one lands live — it shows without a reload.
 */

import { supabase } from '../supabase';
import { sendMessage } from './chatService';
import { buildInviteCtaMessage } from './messageBuilders';
import { getProfileUserId } from '../../utils/identityUtils';

/** Has this side already sent the invite in this room? (Derived from history,
 *  so it survives reloads and navigation.) */
export const hasSentInvite = (chatMessages) =>
  (chatMessages || []).some((m) => m.isInviteCta && m.sender === 'profile');

/**
 * Send the invite: shown at once (addLocal), then the DB write — which is the
 * delivery. Returns sendMessage's { error }.
 *
 * @param {string} chatRoomId
 * @param {(message: Object) => void} addLocal - optimistic add to this room's messages
 */
export const sendRoyaleInvite = async (chatRoomId, addLocal) => {
  const message = buildInviteCtaMessage({ senderId: getProfileUserId() });
  addLocal(message);
  return sendMessage(chatRoomId, message);
};

// Realtime filters take a bounded list; a Simple Profile has a handful of rooms.
const MAX_ROOMS = 100;

/**
 * The receiving side, from the server (041 my_invite_status): the room an
 * admin's invite reached this person in (null when none), and whether their
 * number is confirmed into a live pending entry. The card shows only what the
 * gate would honour.
 *
 * @returns {Promise<{ roomId: string|null, pendingLive: boolean }>}
 */
export const fetchMyInviteStatus = async () => {
  const { data, error } = await supabase.rpc('my_invite_status');
  if (error) {
    console.error('[INVITE] my_invite_status failed:', error.message);
    return { roomId: null, pendingLive: false };
  }
  return { roomId: data?.invite_room || null, pendingLive: !!data?.pending_live };
};

/**
 * Live: call onInvite() when an invite lands in one of these rooms — the cue
 * to ask the server again (fetchMyInviteStatus decides what counts). One
 * channel for all of them (a filtered postgres_changes subscription).
 * Returns the unsubscribe.
 */
export const subscribeToInvites = (roomIds, ownId, onInvite) => {
  const rooms = (roomIds || []).slice(0, MAX_ROOMS);
  if (rooms.length === 0) return () => {};
  const channel = supabase
    .channel(`invites-${ownId}-${Date.now()}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `chat_room_id=in.(${rooms.join(',')})`,
      },
      (payload) => {
        const row = payload.new;
        if (row?.content?.isInviteCta === true && row.sender_id !== ownId) onInvite();
      }
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
};
